import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, RefreshCw, User, Activity } from 'lucide-react';
import { toast } from 'sonner';
import ExportButton from '@/components/ExportButton';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLORS = {
  CREATE: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  DELETE: 'bg-red-100 text-red-800',
  LOGIN: 'bg-purple-100 text-purple-800',
  PUBLISH: 'bg-cyan-100 text-cyan-800',
  EXPORT: 'bg-yellow-100 text-yellow-800',
  UPLOAD: 'bg-orange-100 text-orange-800',
  DEFAULT: 'bg-gray-100 text-gray-800',
};

export default function AuditLog() {
  const { token, selectedCondominium } = useAuth();
  const condoId = selectedCondominium?.condominium?.id;
  const userRole = selectedCondominium?.role || 'RESIDENT';
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  
  // Filters
  const [searchUser, setSearchUser] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const isAdmin = userRole === 'ADMIN';

  const fetchLogs = useCallback(async () => {
    if (!condoId || !isAdmin) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (searchUser) params.append('user_search', searchUser);
      if (actionFilter) params.append('action', actionFilter);
      if (dateFrom) params.append('from_date', dateFrom);
      if (dateTo) params.append('to_date', dateTo);
      
      const res = await fetch(
        `${API_URL}/api/exports/${condoId}/audit-log?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      } else {
        toast.error('Errore nel caricamento dei log');
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  }, [condoId, token, page, searchUser, actionFilter, dateFrom, dateTo, isAdmin]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (action) => {
    return ACTION_COLORS[action] || ACTION_COLORS.DEFAULT;
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <ClipboardList className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium mb-2">Accesso non autorizzato</h3>
            <p className="text-gray-500">
              Solo gli amministratori possono visualizzare il registro audit.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Audit Log"
        subtitle="Registro delle attività"
        actions={
          <>
            <ExportButton
            condominiumId={condoId}
            exportType="audit-log"
            title="Esporta"
            formats={['print', 'pdf', 'csv']}
            showDateRange={true}
            showArchive={true}
            filters={{ action: actionFilter }}
            additionalParams={{ from_date: dateFrom, to_date: dateTo }}
            token={token}
            variant="outline"
          />
            <Button variant="outline" size="icon" onClick={fetchLogs}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Cerca Utente</Label>
              <Input
                placeholder="Email o ID..."
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Azione</Label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Tutte</SelectItem>
                  <SelectItem value="CREATE">Creazione</SelectItem>
                  <SelectItem value="UPDATE">Modifica</SelectItem>
                  <SelectItem value="DELETE">Eliminazione</SelectItem>
                  <SelectItem value="LOGIN">Login</SelectItem>
                  <SelectItem value="PUBLISH">Pubblicazione</SelectItem>
                  <SelectItem value="EXPORT">Export</SelectItem>
                  <SelectItem value="UPLOAD">Upload</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Da</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">A</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-2 flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchUser('');
                  setActionFilter('');
                  setDateFrom('');
                  setDateTo('');
                }}
                className="h-9 w-full"
              >
                Azzera Filtri
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Attività Registrate
            </CardTitle>
            <Badge variant="outline">{total} risultati</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              Caricamento...
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              Nessuna attività trovata
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[160px]">Data/Ora</TableHead>
                    <TableHead>Utente</TableHead>
                    <TableHead className="w-[100px]">Azione</TableHead>
                    <TableHead>Entità</TableHead>
                    <TableHead>Dettagli</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs">
                        {formatDate(log.timestamp)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3 text-gray-400" />
                          <span className="truncate max-w-[150px]">
                            {log.user_email || log.user_id?.slice(0, 8) || '—'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getActionColor(log.action)}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        <span className="text-gray-600">{log.entity_type}</span>
                        {log.entity_id && (
                          <span className="text-xs text-gray-400 ml-1">
                            #{log.entity_id.slice(0, 8)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500 max-w-[200px] truncate">
                        {typeof log.details === 'object' 
                          ? JSON.stringify(log.details).slice(0, 50) 
                          : log.details || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {total > pageSize && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Pagina {page} di {Math.ceil(total / pageSize)}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                      Precedente
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= Math.ceil(total / pageSize)}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Successiva
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
